/**
 * Model Storage Service
 *
 * IndexedDB-based storage for IDF models with full CRUD operations.
 */

import type { StoredModel, ModelMetadata } from './types';

const DB_NAME = 'envelop-storage';
const DB_VERSION = 1;
const MODELS_STORE = 'models';
const RECENT_STORE = 'recent';
const SETTINGS_STORE = 'settings';

/**
 * Generate a unique ID for models.
 */
export function generateModelId(): string {
  return `model_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Open the IndexedDB database.
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open database: ${request.error?.message ?? 'Unknown error'}`));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Models store
      if (!db.objectStoreNames.contains(MODELS_STORE)) {
        const modelsStore = db.createObjectStore(MODELS_STORE, { keyPath: 'id' });
        modelsStore.createIndex('name', 'name', { unique: false });
        modelsStore.createIndex('lastModified', 'lastModified', { unique: false });
      }

      // Recent files store
      if (!db.objectStoreNames.contains(RECENT_STORE)) {
        const recentStore = db.createObjectStore(RECENT_STORE, { keyPath: 'id' });
        recentStore.createIndex('lastOpened', 'lastOpened', { unique: false });
      }

      // Settings store
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Model Storage class for managing stored models.
 */
export class ModelStorage {
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Get the database instance, opening it if necessary.
   */
  private async getDb(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDatabase();
    }
    return this.dbPromise;
  }

  /**
   * Save a model to storage.
   */
  async saveModel(model: StoredModel): Promise<void> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(MODELS_STORE, 'readwrite');
      const store = tx.objectStore(MODELS_STORE);

      // Ensure dates are serializable
      const modelToStore = {
        ...model,
        lastModified: model.lastModified instanceof Date ? model.lastModified : new Date(model.lastModified),
        createdAt: model.createdAt instanceof Date ? model.createdAt : new Date(model.createdAt),
      };

      const request = store.put(modelToStore);

      request.onerror = () => {
        reject(new Error(`Failed to save model: ${request.error?.message ?? 'Unknown error'}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Load a model from storage by ID.
   */
  async loadModel(id: string): Promise<StoredModel | null> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(MODELS_STORE, 'readonly');
      const store = tx.objectStore(MODELS_STORE);
      const request = store.get(id);

      request.onerror = () => {
        reject(new Error(`Failed to load model: ${request.error?.message ?? 'Unknown error'}`));
      };

      request.onsuccess = () => {
        const result = request.result as StoredModel | undefined;
        if (result) {
          // Convert stored dates back to Date objects
          result.lastModified = new Date(result.lastModified);
          result.createdAt = new Date(result.createdAt);
        }
        resolve(result ?? null);
      };
    });
  }

  /**
   * List all stored models.
   */
  async listModels(): Promise<StoredModel[]> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(MODELS_STORE, 'readonly');
      const store = tx.objectStore(MODELS_STORE);
      const request = store.getAll();

      request.onerror = () => {
        reject(new Error(`Failed to list models: ${request.error?.message ?? 'Unknown error'}`));
      };

      request.onsuccess = () => {
        const models = (request.result as StoredModel[]).map((model) => ({
          ...model,
          lastModified: new Date(model.lastModified),
          createdAt: new Date(model.createdAt),
        }));
        // Sort by last modified, newest first
        models.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());
        resolve(models);
      };
    });
  }

  /**
   * Delete a model from storage.
   */
  async deleteModel(id: string): Promise<void> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(MODELS_STORE, 'readwrite');
      const store = tx.objectStore(MODELS_STORE);
      const request = store.delete(id);

      request.onerror = () => {
        reject(new Error(`Failed to delete model: ${request.error?.message ?? 'Unknown error'}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Check if a model exists.
   */
  async modelExists(id: string): Promise<boolean> {
    const model = await this.loadModel(id);
    return model !== null;
  }

  /**
   * Get the total number of stored models.
   */
  async getModelCount(): Promise<number> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(MODELS_STORE, 'readonly');
      const store = tx.objectStore(MODELS_STORE);
      const request = store.count();

      request.onerror = () => {
        reject(new Error(`Failed to count models: ${request.error?.message ?? 'Unknown error'}`));
      };

      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  }

  /**
   * Search models by name.
   */
  async searchModels(query: string): Promise<StoredModel[]> {
    const models = await this.listModels();
    const lowerQuery = query.toLowerCase();
    return models.filter((model) => model.name.toLowerCase().includes(lowerQuery));
  }

  /**
   * Clear all stored models.
   */
  async clearAll(): Promise<void> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(MODELS_STORE, 'readwrite');
      const store = tx.objectStore(MODELS_STORE);
      const request = store.clear();

      request.onerror = () => {
        reject(new Error(`Failed to clear models: ${request.error?.message ?? 'Unknown error'}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }
}

/**
 * Extract metadata from IDF content.
 */
export function extractModelMetadata(content: string): ModelMetadata {
  let version = '';
  let zoneCount = 0;
  let surfaceCount = 0;
  let objectCount = 0;

  // Simple regex-based extraction
  const lines = content.split('\n');
  let inObject = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip comments and empty lines
    if (trimmed.startsWith('!') || trimmed === '') {
      continue;
    }

    // Check for object start (class name followed by comma)
    if (!inObject && trimmed.includes(',') && !trimmed.startsWith(',')) {
      const className = trimmed.split(',')[0]?.trim().toLowerCase() ?? '';

      if (className === 'version') {
        // Extract version from next field
        const match = content.match(/Version\s*,\s*([^;,]+)/i);
        if (match?.[1]) {
          version = match[1].trim();
        }
      } else if (className === 'zone') {
        zoneCount++;
      } else if (
        className.startsWith('buildingsurface:') ||
        className.startsWith('fenestrationsurface:') ||
        className === 'floor:detailed' ||
        className === 'wall:detailed' ||
        className === 'roof:detailed' ||
        className === 'ceiling:detailed'
      ) {
        surfaceCount++;
      }

      objectCount++;
      inObject = true;
    }

    // Check for object end
    if (trimmed.includes(';')) {
      inObject = false;
    }
  }

  return {
    version,
    zoneCount,
    surfaceCount,
    objectCount,
  };
}

/**
 * Create a StoredModel object from IDF content.
 */
export function createStoredModel(
  content: string,
  name: string,
  id?: string,
  thumbnail?: string
): StoredModel {
  const now = new Date();
  const metadata = extractModelMetadata(content);

  return {
    id: id ?? generateModelId(),
    name,
    content,
    lastModified: now,
    createdAt: now,
    thumbnail,
    metadata,
  };
}

// Singleton instance
export const modelStorage = new ModelStorage();
